import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { Clock, Plus, Search } from 'lucide-react';
import { Input } from '../../components/ui/Field';
import { cn } from '../../lib/cn';
import { hcFillSelected } from '../../lib/forcedColors';
import { SUBHEADER_CLS } from '../approval/statusTabs';
import { CategoryDot } from '../categories/CategoryDot';
import { CategoryForm } from './CategoryForm';
import { filterCategories, type ScheduleId } from './scheduleDraft';

/** Past this many categories a search box appears above the list (spec). */
export const SEARCH_AFTER = 6;

/** The categories the list shows: those matching the search while there is one, else all. */
export function shownCategories<T extends { name: string }>(categories: readonly T[], query: string): T[] {
  return filterCategories(categories, categories.length > SEARCH_AFTER ? query : '');
}

/** A tab's key in the shared map of tab elements: '' for General (no category id is empty). */
export const tabKey = (id: ScheduleId) => id ?? '';

/**
 * The row a key moves to from `current`, across General and the shown categories as one list, or
 * undefined for a key the sidebar does not handle. Up and Down wrap; Home is General, End the last row.
 */
export function rowAfterKey(
  rows: readonly ScheduleId[],
  current: ScheduleId,
  key: string,
): ScheduleId | undefined {
  const at = rows.indexOf(current);
  if (at === -1 || rows.length === 0) return undefined;
  switch (key) {
    case 'ArrowDown':
      return rows[(at + 1) % rows.length];
    case 'ArrowUp':
      return rows[(at - 1 + rows.length) % rows.length];
    case 'Home':
      return rows[0];
    case 'End':
      return rows[rows.length - 1];
    default:
      return undefined;
  }
}

export interface SidebarCategory {
  id: string;
  name: string;
  color: string;
  /** Its enabled posting times. */
  count: number;
}

/**
 * The Weekly slots card's left column: General, then the categories, each with its count of enabled
 * posting times, and (for admins) "Add category".
 *
 * Two vertical tablists share the one panel beside them: "Schedules" holds General, "Categories" the
 * category rows. A tablist may hold only tabs, and the search box, the empty hint and the labels sit
 * between the two; a list with no rows is not drawn at all. To the keyboard they are one list: a single
 * Tab stop (the selected row, or General while the selected one is filtered out), and Up/Down move the
 * selection and the focus together across both, as the panel follows at once (every schedule lives in
 * one draft, so nothing is lost by passing through).
 */
export function ScheduleSidebar({
  generalCount,
  categories,
  selected,
  onSelect,
  query,
  onQueryChange,
  tabId,
  panelId,
  tabRefs,
  canEdit,
  addDisabled,
  onAdd,
  className,
}: {
  generalCount: number;
  /** Every category, in creation order. */
  categories: SidebarCategory[];
  selected: ScheduleId;
  onSelect: (id: ScheduleId) => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** The id of a schedule's tab: the panel is labelled by the selected one. */
  tabId: (id: ScheduleId) => string;
  panelId: string;
  /** Each tab's element, by tabKey(): the page moves the focus to a tab after adding or deleting. */
  tabRefs: MutableRefObject<Map<string, HTMLButtonElement>>;
  /** Workspace admins only: the "Add category" button and form. */
  canEdit: boolean;
  /** While the schedule is saving, nothing is added. */
  addDisabled: boolean;
  /** Creates the category; rejects with the reason to show in the form. */
  onAdd: (name: string, color: string) => Promise<void>;
  className?: string;
}) {
  const schedulesLabel = useId();
  const categoriesLabel = useId();

  const showSearch = categories.length > SEARCH_AFTER;
  const shown = shownCategories(categories, query);
  const rows: ScheduleId[] = [null, ...shown.map((c) => c.id)];
  const tabStop: ScheduleId = selected !== null && shown.some((c) => c.id === selected) ? selected : null;

  const [adding, setAdding] = useState(false);
  const addButton = useRef<HTMLButtonElement>(null);
  // The "Add category" button is drawn again only once the form is gone, so the focus goes back to it
  // after that render, not in the handler.
  const [refocusAdd, setRefocusAdd] = useState(false);
  useEffect(() => {
    if (!refocusAdd) return;
    addButton.current?.focus();
    setRefocusAdd(false);
  }, [refocusAdd]);

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, id: ScheduleId) => {
    // Alt+Up and the like belong to the browser; modified keys are never the list's.
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const next = rowAfterKey(rows, id, e.key);
    if (next === undefined) return;
    e.preventDefault();
    onSelect(next);
    tabRefs.current.get(tabKey(next))?.focus();
  };

  const tab = (id: ScheduleId, name: string, count: number, icon: ReactNode) => {
    const isSelected = id === selected;
    return (
      <button
        key={tabKey(id)}
        ref={(el) => {
          if (el) tabRefs.current.set(tabKey(id), el);
          else tabRefs.current.delete(tabKey(id));
        }}
        type="button"
        role="tab"
        id={tabId(id)}
        aria-selected={isSelected}
        aria-controls={panelId}
        tabIndex={id === tabStop ? 0 : -1}
        onClick={() => onSelect(id)}
        onKeyDown={(e) => onKeyDown(e, id)}
        className={cn(
          'focus-ring flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
          isSelected ? 'bg-tint font-semibold text-tint-fg' : 'text-ink-label hover:bg-hover hover:text-ink',
          isSelected && hcFillSelected,
        )}
      >
        {icon}
        <span className="min-w-0 flex-1 truncate">{name}</span>
        {/* A plain number on screen; read out as what it counts. In forced colours the selected row
            draws its own colours, and the count takes the row's (HighlightText) rather than its grey. */}
        <span aria-hidden className="shrink-0 tabular-nums text-ink-muted forced-colors:[color:inherit]">
          {count}
        </span>
        <span className="sr-only">
          , {count} posting time{count === 1 ? '' : 's'}
        </span>
      </button>
    );
  };

  return (
    <div className={className}>
      <p id={schedulesLabel} className={SUBHEADER_CLS}>
        Schedules
      </p>
      <div role="tablist" aria-orientation="vertical" aria-labelledby={schedulesLabel}>
        {tab(null, 'General', generalCount, <Clock className="h-4 w-4 shrink-0" aria-hidden />)}
      </div>

      <p id={categoriesLabel} className={cn(SUBHEADER_CLS, 'mt-5')}>
        Categories
      </p>
      {showSearch && (
        <label className="relative mb-2 block">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint"
            aria-hidden
          />
          <Input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search categories"
            aria-label="Search categories"
            className="pl-9"
          />
        </label>
      )}
      {categories.length === 0 ? (
        <p className="text-sm text-ink-muted">No categories yet. A category gets its own posting times.</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-ink-muted">No categories match.</p>
      ) : (
        <div
          role="tablist"
          aria-orientation="vertical"
          aria-labelledby={categoriesLabel}
          // Scrolls inside the sidebar so a long list never stretches the page. The padding keeps each
          // row's focus ring inside the scroll box, which would clip it.
          className="-mx-1.5 max-h-[26.875rem] space-y-0.5 overflow-y-auto p-1.5"
        >
          {shown.map((c) => tab(c.id, c.name, c.count, <CategoryDot color={c.color} />))}
        </div>
      )}

      {canEdit &&
        (adding ? (
          <CategoryForm
            label="Add category"
            categories={categories}
            submitLabel="Add"
            onSubmit={async (name, color) => {
              await onAdd(name, color);
              setAdding(false);
            }}
            onCancel={() => {
              setAdding(false);
              setRefocusAdd(true);
            }}
            className="mt-3 rounded-2xl border border-line p-3"
          />
        ) : (
          <button
            ref={addButton}
            type="button"
            onClick={() => setAdding(true)}
            disabled={addDisabled}
            className="focus-ring mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-line-dashed bg-card/60 px-5 py-3 text-sm font-medium text-action transition-colors hover:border-ink-ghost hover:bg-tint hover:text-tint-fg disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-4 w-4" aria-hidden /> Add category
          </button>
        ))}
    </div>
  );
}
